import { Coordinate, TileSize } from "game-common/models"

export interface CameraFollowTuning {
    /** false puts back the camera locked dead on the player. */
    enabled: boolean
    /** Half-size (px) of the box the player can move in without moving the camera. */
    deadZone: number
    /** How far (px) the camera looks ahead of the way the player is walking. */
    lead: number
    /** Seconds for the look-ahead to build or swing round. */
    leadEase: number
    /** How far (px) the camera leans toward the aim of a held ranged attack. */
    aimLead: number
    /** Seconds for the camera to catch up with where it wants to be; 0 = rigid. */
    follow: number
    /**
     * Px of walking for the camera to make up about two-thirds of a change in the ground's height (a hill, hill_lift.ts);
     * 0 = it rises and falls with the player exactly.
     */
    hillEase: number
    /** The most (px) the camera may trail a climb or a descent by. */
    hillLag: number
}

const DEFAULT_TUNING: CameraFollowTuning = {
    enabled: true,
    deadZone: 12,
    lead: 24,
    leadEase: 0.4,
    aimLead: 64,
    follow: 0.12,
    hillEase: 48,
    hillLag: 56,
}

const tuning: CameraFollowTuning = { ...DEFAULT_TUNING }

export const tuneCameraFollow = (next: Partial<CameraFollowTuning> | boolean | null): CameraFollowTuning => {
    if (next === null) Object.assign(tuning, DEFAULT_TUNING)
    else if (typeof next === "boolean") tuning.enabled = next
    else Object.assign(tuning, next)
    return { ...tuning }
}

export interface CameraFollowInput {
    /** Movement intent, as Player's velocity loop leaves it in entity.speed2 (length ~1 at full walk). */
    intent?: Coordinate
    /** Walking pace (px/s) at full intent: entity.speed. */
    speed?: number
    /** Facing of a held ranged attack, in radians; undefined when not aiming. */
    aimAngle?: number
    /** Keeps a point inside the area the camera can centre on (the map-edge clamp). */
    clamp: (point: Coordinate) => Coordinate
    /** Place the camera on the player outright: a room load, a fall. A teleport is caught here. */
    snap?: boolean
}

const ease = (seconds: number, tau: number) => tau <= 0 ? 1 : 1 - Math.exp(-seconds / tau)

const clampLength = (v: Coordinate, max: number): Coordinate => {
    const length = Math.hypot(v.x, v.y)
    return length <= max ? v : { x: v.x * max / length, y: v.y * max / length }
}

/** Ease `v` toward `target` by `fraction` of the gap, but never more than `maxStep` px. */
const approach = (v: Coordinate, target: Coordinate, fraction: number, maxStep: number) => {
    const step = clampLength({ x: (target.x - v.x) * fraction, y: (target.y - v.y) * fraction }, maxStep)
    v.x += step.x
    v.y += step.y
}

/**
 * Decides the point the camera centres on, in place of the player's own location.
 *
 *   THE CAMERA ONLY MOVES WHILE THE PLAYER DOES. Stopping, or letting go of a drawn bow, leaves the
 *     view where it is: easing back to centre slid the whole world under a player standing still,
 *     and that motion nobody asked for is what made it seasick. The view recentres the next time they
 *     walk somewhere.
 *   THE DEAD ZONE is a window that follows the player only once they push on its edge, so a fidget,
 *     a lunge or a correction from the server doesn't move the whole world. It closes up while the
 *     player walks: a window dragged along behind them would eat its own width out of the look-ahead.
 *   THE LEAD puts the camera ahead of the way the player is heading, from movement INTENT rather than
 *     measured motion: knockback and pushes don't swing the view, and the position-error smoothing in
 *     client.ts, which hands the camera the true and the rendered location in turn, adds no noise. It
 *     swings round no faster than the player walks, so turning on the spot can't whip the view past
 *     twice walking pace.
 *   THE FOLLOW eases the camera after that point. On its own it would be worse than a locked camera:
 *     a lagging camera leaves you seeing less of where you are going. So the target also carries the
 *     distance the easing trails a walking player by (velocity x follow), and `lead` is what you
 *     actually see ahead. That term tracks the body's real velocity, so it is gone the moment the body
 *     stops -- just as the lag is -- and the camera comes to rest exactly when the player does.
 *
 * The target is clamped to the map edge BEFORE easing so the camera slows into an edge instead of
 * stopping dead on it. Times are frame-rate independent.
 */
export class CameraFollow {
    /** A step this long between two calls is a teleport, not a walk: the camera goes straight there. */
    static readonly JUMP_DISTANCE = TileSize
    /** Intent above this is walking; below it (released keys coasting to a halt) the lead holds. */
    private static readonly WALKING = 0.5
    private camera?: Coordinate
    private anchor?: Coordinate
    private last?: Coordinate
    private moveLead: Coordinate = { x: 0, y: 0 }
    private aimLean: Coordinate = { x: 0, y: 0 }
    private walking = 0
    private ts = 0
    private lift?: number
    private liftTs = 0
    /** focus() placed the camera outright this frame: the hill's lift goes straight there too. */
    private placed = true

    focus = (location: Coordinate, now: number, input: CameraFollowInput): Coordinate => {
        const jumped = this.last &&
            Math.hypot(location.x - this.last.x, location.y - this.last.y) >= CameraFollow.JUMP_DISTANCE
        this.last = { x: location.x, y: location.y }
        this.placed = !tuning.enabled || !!input.snap || !!jumped || !this.camera
        if (this.placed) return this.place(location, now, input.clamp)
        const seconds = Math.max(0, now - this.ts) / 1000
        this.ts = now

        const intent = clampLength(input.intent ?? { x: 0, y: 0 }, 1)
        const pace = Math.hypot(intent.x, intent.y)
        const walking = pace > CameraFollow.WALKING
        const leadEase = ease(seconds, tuning.leadEase)
        const maxStep = input.speed ? input.speed * seconds : Infinity
        if (walking) {
            const lead = tuning.lead / pace
            approach(this.moveLead, { x: intent.x * lead, y: intent.y * lead }, leadEase, maxStep)
        }
        if (input.aimAngle !== undefined) {
            const aim = { x: Math.cos(input.aimAngle) * tuning.aimLead, y: Math.sin(input.aimAngle) * tuning.aimLead }
            approach(this.aimLean, aim, leadEase, maxStep)
        } else if (walking) {
            approach(this.aimLean, { x: 0, y: 0 }, leadEase, maxStep)
        }
        this.walking += (pace - this.walking) * leadEase

        const zone = tuning.deadZone * (1 - this.walking)
        const anchor = this.anchor
        anchor.x = Math.min(Math.max(anchor.x, location.x - zone), location.x + zone)
        anchor.y = Math.min(Math.max(anchor.y, location.y - zone), location.y + zone)

        const offset = clampLength(
            { x: this.moveLead.x + this.aimLean.x, y: this.moveLead.y + this.aimLean.y },
            Math.max(tuning.lead, tuning.aimLead))
        const trail = (input.speed ?? 0) * tuning.follow
        const target = input.clamp({
            x: anchor.x + offset.x + intent.x * trail,
            y: anchor.y + offset.y + intent.y * trail,
        })
        const follow = ease(seconds, tuning.follow)
        this.camera.x += (target.x - this.camera.x) * follow
        this.camera.y += (target.y - this.camera.y) * follow
        return { ...this.camera }
    }

    /**
     * How far up the camera rises for the ground under the player (hill_lift.ts). Followed exactly, a slope threw the
     * whole world up a level in a third of a second. Instead the lift is eased BY DISTANCE WALKED, from intent like the
     * lead: up a slope the camera rises at a fraction of the climb and makes up the rest over the next few steps, and a
     * player standing still -- halfway up or at the top -- holds the view where it is, by the rule above. Call it after
     * focus() each frame; a room load, a teleport or a fall places it outright.
     */
    hillLift = (target: number, now: number, input: Pick<CameraFollowInput, "intent" | "speed">): number => {
        const seconds = Math.max(0, now - this.liftTs) / 1000
        this.liftTs = now
        if (this.placed || this.lift === undefined || tuning.hillEase <= 0) return this.lift = target
        const intent = clampLength(input.intent ?? { x: 0, y: 0 }, 1)
        const walked = Math.hypot(intent.x, intent.y) * (input.speed ?? 0) * seconds
        this.lift += (target - this.lift) * (1 - Math.exp(-walked / tuning.hillEase))
        this.lift = Math.min(Math.max(this.lift, target - tuning.hillLag), target + tuning.hillLag)
        return this.lift
    }

    reset = () => {
        this.camera = undefined
        this.anchor = undefined
        this.last = undefined
        this.lift = undefined
    }

    private place = (location: Coordinate, now: number, clamp: (point: Coordinate) => Coordinate): Coordinate => {
        this.camera = clamp(location)
        this.anchor = { x: location.x, y: location.y }
        this.moveLead = { x: 0, y: 0 }
        this.aimLean = { x: 0, y: 0 }
        this.walking = 0
        this.ts = now
        return { ...this.camera }
    }
}

// Browser-console tuning, in the same shape as window.playerRing / window.spriteShadow:
//
//     cameraFollow = false                  // the old camera, locked on the player
//     cameraFollow = { lead: 64 }           // look further ahead
//     cameraFollow = { deadZone: 0 }        // no free box
//     cameraFollow = { follow: 0 }          // no easing
//     cameraFollow = { hillEase: 0 }        // rise and fall with a hill exactly
//     cameraFollow = null                   // back to the defaults
//     cameraFollow                          // read the active values back
//
// Guarded so it is a no-op in the Node test harness.
if (typeof window !== "undefined") {
    Object.defineProperty(window, "cameraFollow", {
        get: () => ({ ...tuning }),
        set: (next: Partial<CameraFollowTuning> | boolean | null) => {
            console.log("[cameraFollow]", tuneCameraFollow(next))
        },
        configurable: true,
    })
}
